import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { CircleAdd } from '@randstad-lean-mobile-factory/react-assets/dist/icons';
import {
  Button,
  DataTable,
  Modal,
  ModalActions,
  ModalContent,
  Pagination,
  SearchInput,
  ToggleMenu,
  ToggleMenuItem,
} from '@randstad-lean-mobile-factory/react-components-core';
import {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  SortingState,
  Table,
} from '@tanstack/react-table';

import DeleteRoutingModalComponent from 'src/Containers/Modals/DeleteRoutingsModal/DeleteRoutingsModal.component';

import { useDeleteRoutingById } from '../../../Hooks/Routings/useDeleteRoutingById';
import { useFetchRoutings } from '../../../Hooks/Routings/useFetchRoutings';
import { useFetchUniqueColumnValues } from '../../../Hooks/Routings/useFetchUniqueColumnValues';
import {
  AppelMedicalEventSource,
  FetchRoutingsSortCriteria,
  GetRoutingsBody,
  RoutingInfos,
} from '../../../Services/API';
import AddRoutingModalComponent from '../../Modals/AddRoutingsModal/AddRoutingModal.component';
import EditRoutingsModalComponent from '../../Modals/EditRoutingsModal/EditRoutingsModal.component';
import TopBar from '../TopBar';

import styles from './RoutingsSection.module.scss';
import { formatFilters, formatSorting } from './utils';

const RoutingsSection = () => {
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [openNewRouting, setOpenNewRouting] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [selectedSource, setSelectedSource] = useState(AppelMedicalEventSource.ARTEMIHS);

  const tableRef = useRef<Table<RoutingInfos>>(null);

  const searchParams = useMemo(
    (): Omit<GetRoutingsBody, 'limit' | 'page'> => ({
      ...formatFilters(columnFilters),
      globalFilter,
      sort: formatSorting(sorting),
      source: selectedSource,
    }),
    [columnFilters, globalFilter, sorting, selectedSource]
  );

  const paginatedSearchParams = useMemo(
    () => ({
      ...searchParams,
      limit: pagination.pageSize,
      page: pagination.pageIndex,
    }),
    [pagination.pageIndex, pagination.pageSize, searchParams]
  );

  const { data } = useFetchRoutings({ ...paginatedSearchParams });
  const deleteRoutingMutation = useDeleteRoutingById();
  const { data: uniqueColumnValues } = useFetchUniqueColumnValues();

  const getFacetedUniqueValues = useCallback(
    (_: Table<RoutingInfos>, columnId: string) => {
      if (columnId === FetchRoutingsSortCriteria['routing.clientName'])
        return () => new Map(uniqueColumnValues?.clientNames.map(clientName => [clientName, 1]));
      if (
        columnId === FetchRoutingsSortCriteria['routing.service'] &&
        selectedSource === AppelMedicalEventSource.HUBLO
      )
        return () => new Map(uniqueColumnValues?.services.map(service => [service, 1]));
      if (columnId === FetchRoutingsSortCriteria['routing.siret'])
        return () => new Map(uniqueColumnValues?.sirets.map(siret => [siret, 1]));
      if (columnId === FetchRoutingsSortCriteria['routing.qualification'])
        return () =>
          new Map(uniqueColumnValues?.qualifications.map(qualification => [qualification, 1]));
      if (columnId === FetchRoutingsSortCriteria['routing.brandCode'])
        return () => new Map(uniqueColumnValues?.brands.map(brand => [brand, 1]));
      if (columnId === FetchRoutingsSortCriteria['agency.id'])
        return () => new Map(uniqueColumnValues?.agenciesIds.map(agencyId => [agencyId, 1]));
      return () => new Map<string, number>();
    },
    [uniqueColumnValues, selectedSource]
  );

  useEffect(() => tableRef.current?.setGlobalFilter(globalFilter), [globalFilter]);

  useEffect(() => {
    setPagination(previousPagination =>
      previousPagination.pageIndex === 0
        ? previousPagination
        : { ...previousPagination, pageIndex: 0 }
    );
  }, [selectedSource, columnFilters, globalFilter, sorting]);

  const columns = useMemo(
    (): ColumnDef<RoutingInfos>[] => [
      {
        id: FetchRoutingsSortCriteria['routing.qualification'],
        accessorKey: 'qualification',
        header: 'qualification',
        cell: ({ row }) => {
          return <span className={styles.cellText}>{row.original.qualification}</span>;
        },
        enableColumnFilter: Boolean(uniqueColumnValues),
        filterFn: 'arrIncludes',
      },
      {
        id: FetchRoutingsSortCriteria['routing.clientName'],
        accessorKey: 'clientName',
        header: 'nom client',
        cell: ({ row }) => {
          return <span className={styles.cell}>{row.original.clientName}</span>;
        },
        enableColumnFilter: Boolean(uniqueColumnValues),
        filterFn: 'arrIncludes',
      },
      {
        id: FetchRoutingsSortCriteria['routing.siret'],
        accessorKey: 'siret',
        header: 'siret',
        cell: ({ row }) => {
          return <span className={styles.cell}>{row.original.siret}</span>;
        },
        enableColumnFilter: Boolean(uniqueColumnValues),
        filterFn: 'arrIncludes',
      },
      ...(selectedSource === AppelMedicalEventSource.HUBLO
        ? [
            {
              id: FetchRoutingsSortCriteria['routing.service'],
              accessorKey: 'service',
              cell: ({ row }) => {
                return <span className={styles.cell}>{row.original.service}</span>;
              },
              header: 'service',
              enableColumnFilter: Boolean(uniqueColumnValues),
              filterFn: 'arrIncludes',
            } as ColumnDef<RoutingInfos>,
          ]
        : []),
      {
        id: FetchRoutingsSortCriteria['agency.id'],
        accessorKey: 'agencyId',
        header: 'code agence',
        enableColumnFilter: Boolean(uniqueColumnValues),
        filterFn: 'arrIncludes',
      },
      {
        id: FetchRoutingsSortCriteria['routing.brandCode'],
        accessorKey: 'brandCode',
        header: 'marque',
        enableColumnFilter: Boolean(uniqueColumnValues),
        filterFn: 'arrIncludes',
      },

      {
        id: 'actions',
        header: 'actions',
        size: 50,
        cell: ({ row }) => (
          <>
            <EditRoutingsModalComponent routingId={row.original.routingId} />
            <DeleteRoutingModalComponent routingId={row.original.routingId} />
            <Modal size="small" open={openDeleteModal} onClose={() => setOpenDeleteModal(false)}>
              <ModalContent>
                <div>Êtes-vous sûr de vouloir supprimer ce routage ?</div>
              </ModalContent>
              <ModalActions side="right">
                <Button variant="secondary" onClick={() => setOpenDeleteModal(false)}>
                  annuler
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    deleteRoutingMutation.mutate(row.original.routingId);
                    setOpenDeleteModal(false);
                  }}
                >
                  supprimer
                </Button>
              </ModalActions>
            </Modal>
          </>
        ),
      },
    ],
    [deleteRoutingMutation, openDeleteModal, uniqueColumnValues, selectedSource]
  );

  return (
    <>
      <TopBar />
      <div className={styles.content}>
        <div className={styles.tableOptions}>
          <div className={styles.topRow}>
            <div className={styles.toggleMenuWrapper}>
              <ToggleMenu value={selectedSource} onChange={setSelectedSource}>
                <ToggleMenuItem value={AppelMedicalEventSource.ARTEMIHS}>artemihs</ToggleMenuItem>
                <ToggleMenuItem value={AppelMedicalEventSource.HUBLO}>hublo</ToggleMenuItem>
              </ToggleMenu>
            </div>
            <div className={styles.row}>
              <div className={styles.routingOptions}>
                <Button variant="primary" onClick={() => setOpenNewRouting(true)}>
                  <CircleAdd variant="line" color="white" /> ajouter un routage
                </Button>
                <AddRoutingModalComponent
                  isOpen={openNewRouting}
                  setIsOpen={setOpenNewRouting}
                  selectedSource={selectedSource}
                />
                <SearchInput
                  size="medium"
                  value={globalFilter}
                  onValueChange={setGlobalFilter}
                  placeholder="rechercher"
                  collapsed
                />
              </div>
            </div>
          </div>
          <Pagination
            currentPage={pagination.pageIndex}
            pageCount={Math.ceil((data?.count ?? 1) / pagination.pageSize)}
            setCurrentPage={page =>
              setPagination(previousPagination => ({ ...previousPagination, pageIndex: page }))
            }
          />
        </div>
        <div className={styles.tableContainer}>
          <DataTable
            ref={tableRef}
            className={styles.table}
            state={{ columnFilters, sorting, pagination, globalFilter }}
            onSortingChange={setSorting}
            onColumnFiltersChange={setColumnFilters}
            enablePagination
            manualFiltering
            manualPagination
            manualSorting
            onPaginationChange={setPagination}
            getFacetedUniqueValues={getFacetedUniqueValues}
            enableMultiSort={false}
            empty="aucun résultat ne correspond à votre recherche"
            initialState={{ pagination }}
            columns={columns}
            data={data?.routings ?? []}
            size="medium"
          />
        </div>
      </div>
    </>
  );
};

export default RoutingsSection;
